/**
 * Data layer for `capi-test-event-mode-for-verification`. Thin typed fetch wrappers
 * over /api/v1/capi-test-event-mode-for-verification.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapiTestEventRow {
	id: string;
	tenant_id: string;
	event_id: string;
	event_name: string;
	test_mode: number;
	payload: string;
	verification_code: string | null;
	received_at: number;
	created_at: number;
}

export interface ListCapiTestEventsResponse {
	items: CapiTestEventRow[];
}

export interface CreateCapiTestEventBody {
	event_id: string;
	event_name: string;
	test_mode?: number | boolean | string;
	verification_code?: string;
	payload?: Record<string, unknown>;
}

export interface CreateCapiTestEventResponse {
	ok: boolean;
	event_id: string;
	test_mode: number;
}

export type TestModeFilter = "all" | "test" | "live";

const BASE = "/api/v1/capi-test-event-mode-for-verification";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(filter: TestModeFilter = "all"): Promise<ListCapiTestEventsResponse> {
	const qs =
		filter === "test" ? "?test_mode=1" : filter === "live" ? "?test_mode=0" : "";
	const res = await fetch(`${BASE}${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapiTestEventsResponse>(res);
}

export async function create(body: CreateCapiTestEventBody): Promise<CreateCapiTestEventResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCapiTestEventResponse>(res);
}

export async function getOne(event_id: string): Promise<CapiTestEventRow> {
	const res = await fetch(`${BASE}/${encodeURIComponent(event_id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CapiTestEventRow>(res);
}

export const capiTestEventModeForVerificationQueryKeys = {
	all: ["capi-test-event-mode-for-verification"] as const,
	list: (filter: TestModeFilter = "all") =>
		[...capiTestEventModeForVerificationQueryKeys.all, "list", filter] as const,
	detail: (event_id: string) =>
		[...capiTestEventModeForVerificationQueryKeys.all, "detail", event_id] as const,
};
